import { EContractStatus, EManagementType } from "api/enums";
import { IContractData } from "api/interfaces";
import { lang } from "i18nLang";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import {
  ContractAttachmentStore,
  ContractIntegrationStore,
  ContractRowStore,
  ContractStore,
  ContractIntegrationStore as Integration,
  MeasurementListStore,
  MessageStore,
} from "store";
import { inputTextToDate } from "utils";
import useValidations from "../../../hooks/useValidations";
import { PageLoading } from "../../UI/Page";
import TabPanel from "../../UI/Tabs/TabPanel";
import TabsStepper from "../../UI/Tabs/TabsStepper";
import FormCard from "./FormCard";
import FormContract from "./FormContract";
import FormIntegration from "./FormIntegration";
import FormModal from "./FormModal";

interface IProps {
  /** Propriedade que indica se o formulário está no modo de edição */
  editing: boolean;
  /** Propriedade que define se o formulário será aberto como modal ou não */
  modal: boolean;
  /** Propriedade que indica se o formulário está aberto ou fechado. */
  open: boolean;
  /** Função que será chamada ao fechar o formulário */
  onClose: Function;
}

const steps = [
  { title: lang("contract.label") },
  { title: lang("contract.integration") },
];

const defaultObj: IContractData = {
  contractName: "",
  projectName: "",
  location: "",
  client: { id: 0, name: "" },
  contractDate: new Date(),
  managementType: EManagementType.GLOBAL,
  status: EContractStatus.AGUARDANDO,
};

const ContractForm = observer(
  ({ editing = true, modal = true, open, onClose }: IProps) => {
    const [data, setData] = useState(defaultObj);
    const [loading, setLoading] = useState(true);
    const [activeStep, setActiveStep] = useState(0);
    const hasUploadingFile = ContractAttachmentStore.hasFileBeingUploaded();

    const { validated, validateFields, hasErrors, reset } = useValidations({
      contractName: "required",
      projectName: "required",
      client: "required",
      location: "required",
      contractDate: {
        date: true,
        custom: (value) => {
          const date = value instanceof Date ? value : inputTextToDate(value);
          return ContractStore.validateContractDate(date);
        },
      },
      branch: (value) => {
        if (ContractIntegrationStore.hasIntegration && !value) {
          return {
            valid: false,
            text: lang("exceptions.requiredParam", "").trim(),
          };
        }
        return;
      },
    });

    useEffect(() => {
      reset();
      if (open) {
        const currentData = setCurrentContractData();
        setLoading(true);

        ContractAttachmentStore.getContractAttachments();
        ContractStore.getCurrentIntegrationData()
          .then((contractData) => {
            if (contractData && currentData) {
              setData({ ...contractData, ...currentData });
            }
          })
          .catch((error) => {
            console.error(error);
            MessageStore.error(lang("contract.errors.fetchData"));
          })
          .finally(() => {
            setLoading(false);
          });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const setCurrentContractData = () => {
      const contract = ContractStore.getCurrentContract();
      if (contract) {
        const currentData = {
          contractName: contract.name,
          contractDate: contract.date,
          projectName: contract.development.name,
          location: contract.development.local,
          client: {
            id: contract.development.customerId || 0,
            name: contract.development.customer,
          },
          managementType: contract.managementType,
          status: contract.status,
        };

        setData(currentData);
        return currentData;
      }

      return;
    };

    const handleClickClose = async () => {
      setActiveStep(0);
      onClose();
      await ContractAttachmentStore.removeAllUnsavedContractAttachment();
      ContractAttachmentStore.reset();
    };

    const handleInputChange = (ev) => {
      const { name, value } = ev.target;
      handleChangeData(name, value);
    };

    const handleChangeData = (name, value) => {
      const newData = { ...data };
      newData[name] = value;

      if (name === "branch" && value !== data.branch) {
        newData.documentType = undefined;
        newData.client = undefined;
      } else if (name === "client" && typeof value === "string") {
        newData.client = { name: value, id: 0 };
      }

      setData(newData);
    };

    const handleInputBlur = (ev) => {
      const { name, value } = ev.target;
      validateFields({ [name]: value });
    };

    const handleClickNext = () => {
      const { contractName, projectName, location, contractDate } = data;
      const allValid = validateFields({
        contractName,
        projectName,
        location,
        contractDate,
      });

      if (allValid) setActiveStep(activeStep + 1);
    };

    const handleClickFinish = () => {
      if (!validateFields(data, true)) return;

      ContractRowStore.setTablesLoading(true);
      ContractStore.saveContract({ ...data }, editing)
        .then(() => {
          handleClickClose();
        })
        .finally(() => {
          setTimeout(() => {
            ContractRowStore.setTablesLoading(false);
          }, 200);
        });
    };

    const hasMeasurements = MeasurementListStore.getMeasurements().length > 0;
    const isFinished = !Integration.hasIntegration || activeStep === 1;
    const title =
      editing === false
        ? lang("contract.newContractTitle")
        : lang("contract.editContractTitle");

    const renderContent = () =>
      loading ? (
        <PageLoading style={{ padding: "3rem 0" }} />
      ) : (
        <>
          <TabPanel value={activeStep} index={0} justifyContent="center">
            <FormContract
              data={{ ...data, client: data.client?.name || "" }}
              validated={validated}
              editClient={!Integration.hasIntegration}
              isEditing={editing}
              disabledManagmentType={hasMeasurements}
              onInputChange={handleInputChange}
              onInputBlur={handleInputBlur}
            />
          </TabPanel>
          <TabPanel value={activeStep} index={1} justifyContent="center">
            <FormIntegration
              data={data}
              validated={validated}
              onInputChange={handleChangeData}
              onInputBlur={handleInputBlur}
            />
          </TabPanel>
        </>
      );

    if (!modal)
      return (
        <FormCard
          title={title}
          TabsStepper={() =>
            Integration.hasIntegration ? (
              <TabsStepper steps={steps} activeStep={activeStep} />
            ) : (
              <></>
            )
          }
          finished={isFinished}
          disabledButton={hasErrors() || hasUploadingFile}
          onNext={handleClickNext}
          onFinish={handleClickFinish}
        >
          {renderContent()}
        </FormCard>
      );

    return (
      <FormModal
        title={title}
        open={open}
        onClose={handleClickClose}
        disabledButton={hasErrors() || hasUploadingFile}
        finished={isFinished}
        onFinish={handleClickFinish}
        onNext={handleClickNext}
      >
        {Integration.hasIntegration && (
          <TabsStepper steps={steps} activeStep={activeStep} />
        )}
        {renderContent()}
      </FormModal>
    );
  }
);

export default ContractForm;
